// src/services/costs/useEstimateEditor.js
// Hook für den Kostenschätzungs-Editor.
import { useState, useCallback, useEffect, useRef } from "react";
import { calculateTotal, aggregateTree } from "./calculations.js";
import { computeControlTotals } from "./controlCalculations.js";
import { getNextChildCode } from "./import.js";
import { updateEstimate } from "../azureCostEstimatesService.js";
import {
  getVariantsByEstimate,
  saveVariant as saveVariantApi,
  updateVariant as updateVariantApi,
  deleteVariant as deleteVariantApi,
  renameVariantsPhase,
  deleteVariantsPhase,
} from "../azureCostEstimateVariantsService.js";
import {
  renameContractsPhase,
  deleteContractsPhase,
  copyContractsPhase,
  getContractsByEstimate,
} from "../azureCostEstimateContractsService.js";
import {
  renamePaymentsPhase,
  deletePaymentsPhase,
  copyPaymentsPhase,
  getPaymentsByEstimate,
} from "../azureCostEstimatePaymentsService.js";

const MAX_UNDO_STACK = 50;

// MwSt-Faktor für die KV-Basis-Übernahme (8.1%), analog getAccuracyFromPhase.
// Bewusst als Konstante: konfigurierbarer Satz ist explizit nicht im Scope (KOST-006).
const MWST_FACTOR = 1.081;

const DEFAULT_PHASES = [
  "21 - Machbarkeit",
  "31 - Vorprojekt",
  "32 - Bauprojekt",
  "33 - Bewilligung",
  "41 - Ausschreibung",
];

// Auto-Save (Kostenschaetzung und Kostenkontrolle), damit niemand mehr manuell
// speichern muss:
//   1. 30 Sekunden nach der letzten Aenderung (Bearbeitungspause), und
//   2. spaetestens 10 Minuten nach Beginn einer Bearbeitung, auch wenn
//      durchgehend weitergearbeitet wird.
export const AUTO_SAVE_IDLE_MS = 30 * 1000;
export const AUTO_SAVE_MAX_AGE_MS = 10 * 60 * 1000;

export function useEstimateEditor(provider) {
  const [tree, setTree] = useState({});
  const [pendingOps, setPendingOps] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [levelFilter, setLevelFilter] = useState(2);
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [filterSums, setFilterSums] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // "manual" = per Speichern-Button/Ctrl+S, "auto" = durch den Auto-Save-Timer.
  const [lastSaveKind, setLastSaveKind] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPhase, setCurrentPhase] = useState("21 - Machbarkeit");
  const [phases, setPhases] = useState(DEFAULT_PHASES);

  // Kostenkontrolle (Phase 41): wurde die KV-Basis bereits aus Phase 32 übernommen?
  const [kvBaseImported, setKvBaseImported] = useState(false);
  // Objektgliederung der aktuellen Kostenkontroll-Phase (ADR 0064 Nachtrag
  // 2026-09-30). null = an der Phase noch nie gesetzt.
  const [objektgliederung, setObjektgliederung] = useState(null);
  const currentPhaseRef = useRef(currentPhase);
  currentPhaseRef.current = currentPhase;

  // Variant state
  const [variants, setVariants] = useState([]);

  // Custom columns state
  const [customColumns, setCustomColumns] = useState([]);
  const [pendingColumnOps, setPendingColumnOps] = useState([]);

  const estimateIdRef = useRef(null);
  const estimateRef = useRef(null);
  const undoBatchRef = useRef(null);

  const isDirty = pendingOps.length > 0 || pendingColumnOps.length > 0;

  // === Variant helpers ===

  // Detect conflicts: BKP codes appearing in multiple active variants
  const getVariantConflicts = useCallback(() => {
    const activeVariants = variants.filter((v) => v.isActive);
    if (activeVariants.length < 2) return {};

    const codeToVariants = {};
    for (const v of activeVariants) {
      for (const pos of v.positions) {
        if (!codeToVariants[pos.bkpCode]) {
          codeToVariants[pos.bkpCode] = [];
        }
        codeToVariants[pos.bkpCode].push(v.id);
      }
    }

    const conflicts = {};
    for (const [code, variantIds] of Object.entries(codeToVariants)) {
      if (variantIds.length > 1) {
        conflicts[code] = variantIds;
      }
    }
    return conflicts;
  }, [variants]);

  // Get effective value for a BKP code considering active variants
  const getEffectiveValue = useCallback((bkpCode) => {
    const activeVariants = variants.filter((v) => v.isActive);
    // Last active variant with an override wins
    for (let i = activeVariants.length - 1; i >= 0; i--) {
      const pos = activeVariants[i].positions.find((p) => p.bkpCode === bkpCode);
      if (pos) {
        return { variantId: activeVariants[i].id, variantName: activeVariants[i].name, overrides: pos.overrides };
      }
    }
    return null;
  }, [variants]);

  // Build variant-aware tree: apply active variant overrides to tree nodes.
  // Wenn eine Variante einen Parent-Node übersteuert, werden seine Kinder im
  // Merged-Tree rekursiv deaktiviert - sonst wuerde der aktivierte Parent die
  // Kinder-Totale "verschlucken" (siehe Review Ebene 1/5 und Ebene 2/K-1).
  const applyVariantOverrides = useCallback((baseTree) => {
    const activeVariants = variants.filter((v) => v.isActive);
    if (activeVariants.length === 0) return baseTree;

    const updated = { ...baseTree };

    const deactivateDescendants = (parentCode) => {
      const parent = updated[parentCode];
      if (!parent?.children) return;
      for (const childCode of parent.children) {
        const child = updated[childCode];
        if (child && (child.active || child.totalComputed)) {
          updated[childCode] = { ...child, active: false, totalComputed: 0 };
        }
        deactivateDescendants(childCode);
      }
    };

    for (const variant of activeVariants) {
      for (const pos of variant.positions) {
        const node = updated[pos.bkpCode];
        if (!node) continue;

        const overrides = pos.overrides || {};
        if (Object.keys(overrides).length === 0) continue;

        const mergedNode = { ...node };

        for (const [field, value] of Object.entries(overrides)) {
          const parsedValue = ["quantity", "length", "width", "height", "unitPrice", "factor"].includes(field)
            ? (parseFloat(value) || 0)
            : value;
          mergedNode[field] = parsedValue;
        }

        mergedNode.totalComputed = calculateTotal(mergedNode);
        mergedNode.active = true;
        updated[pos.bkpCode] = mergedNode;

        // Parent-Override: Kinder im Merged-Tree rekursiv stummschalten.
        if (node.children && node.children.length > 0) {
          deactivateDescendants(pos.bkpCode);
        }
      }
    }

    return aggregateTree(updated);
  }, [variants]);

  // === Load variants for current estimate+phase ===
  const loadVariants = useCallback(async (estimateId, phase) => {
    try {
      const loaded = await getVariantsByEstimate(estimateId, phase);
      setVariants(loaded);
      return loaded;
    } catch (err) {
      console.error("Load variants error:", err);
      return [];
    }
  }, []);

  // === Init: Load tree from provider for a specific phase ===
  const init = useCallback(async (estimateId, estimate, phase) => {
    if (!provider || !estimateId) return;
    estimateIdRef.current = estimateId;
    estimateRef.current = estimate;

    // Set phases from dossier record, fallback to defaults
    const dossierPhases = estimate?.phases?.length > 0 ? estimate.phases : DEFAULT_PHASES;
    setPhases(dossierPhases);

    // Determine initial phase
    const initialPhase = phase || estimate?.currentPhase || dossierPhases[0] || "21 - Machbarkeit";
    setCurrentPhase(initialPhase);

    setIsLoading(true);
    setError(null);
    try {
      const isControlPhase = initialPhase.trim().startsWith("41");
      const [loaded, loadedVariants, loadedColumns, controlMeta] = await Promise.all([
        provider.load(estimateId, initialPhase),
        loadVariants(estimateId, initialPhase),
        provider.loadCustomColumns ? provider.loadCustomColumns(estimateId) : [],
        isControlPhase && provider.loadControlMeta
          ? provider.loadControlMeta(estimateId, initialPhase)
          : { kvBaseImported: false, objektgliederung: null },
      ]);
      const aggregated = aggregateTree(loaded);
      setTree(aggregated);
      setPendingOps([]);
      setPendingColumnOps([]);
      setUndoStack([]);
      setCustomColumns(loadedColumns || []);
      setKvBaseImported(!!controlMeta?.kvBaseImported);
      setObjektgliederung(controlMeta?.objektgliederung ?? null);
      // Expand nodes based on default levelFilter.
      // levelFilter >= 5 bedeutet "alles einblenden" - inkl. tieferer Level (6+),
      // die via addChild4Digit entstehen koennen.
      const initExpanded = {};
      Object.values(aggregated).forEach((node) => {
        if (levelFilter >= 5 || node.level < levelFilter) {
          initExpanded[node.code] = true;
        }
      });
      setExpanded(initExpanded);
    } catch (err) {
      console.error("Init error:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [provider, loadVariants]);

  // === Undo Batch ===
  const beginUndoBatch = useCallback(() => {
    undoBatchRef.current = { snapshot: null, pendingOps: [...pendingOps] };
  }, [pendingOps]);

  const endUndoBatch = useCallback(() => {
    const batch = undoBatchRef.current;
    undoBatchRef.current = null;
    if (batch && batch.snapshot) {
      setUndoStack((prev) => {
        const next = [...prev, { type: "batch", snapshot: batch.snapshot, pendingOps: batch.pendingOps }];
        if (next.length > MAX_UNDO_STACK) next.shift();
        return next;
      });
    }
  }, []);

  // === Edit Cell ===
  const editCell = useCallback((code, field, value) => {
    setTree((prev) => {
      const node = prev[code];
      if (!node) return prev;

      // Capture undo snapshot on first edit in batch
      if (undoBatchRef.current && !undoBatchRef.current.snapshot) {
        undoBatchRef.current.snapshot = { ...prev };
      }

      const updated = { ...prev };
      const parsedValue = ["quantity", "length", "width", "height", "unitPrice", "factor"].includes(field)
        ? (parseFloat(value) || 0)
        : value;

      updated[code] = { ...node, [field]: parsedValue };

      // Recalculate total
      const calcFields = ["quantity", "length", "width", "height", "unitPrice", "unit", "factor"];
      if (calcFields.includes(field)) {
        updated[code].totalComputed = calculateTotal(updated[code]);

        // If editing a parent node with calculation field, auto-activate it and deactivate children
        if (node.children && node.children.length > 0) {
          updated[code].active = true;
          const deactivateDescendants = (parentCode) => {
            const parent = updated[parentCode];
            if (!parent?.children) return;
            for (const childCode of parent.children) {
              if (updated[childCode]) {
                updated[childCode] = {
                  ...updated[childCode],
                  active: false,
                  quantity: 0, length: 0, width: 0, height: 0,
                  unitPrice: 0, unit: "", factor: 1,
                  totalComputed: 0,
                };
              }
              deactivateDescendants(childCode);
            }
          };
          deactivateDescendants(code);
        }
      }

      const result = aggregateTree(updated);
      return result;
    });

    // Track pending op
    setPendingOps((prev) => {
      const existing = prev.findIndex((op) => op.code === code);
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = { ...next[existing], kind: "upsert" };
        return next;
      }
      return [...prev, { code, kind: "upsert" }];
    });
  }, []);

  // === Toggle Active ===
  const toggleActive = useCallback((code) => {
    setTree((prev) => {
      const node = prev[code];
      if (!node) return prev;

      if (undoBatchRef.current && !undoBatchRef.current.snapshot) {
        undoBatchRef.current.snapshot = { ...prev };
      }

      const updated = { ...prev };
      updated[code] = {
        ...node,
        active: !node.active,
        totalComputed: !node.active ? calculateTotal(node) : node.totalComputed,
      };

      return aggregateTree(updated);
    });

    setPendingOps((prev) => {
      const existing = prev.findIndex((op) => op.code === code);
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = { ...next[existing], kind: "upsert" };
        return next;
      }
      return [...prev, { code, kind: "upsert" }];
    });
  }, []);

  // === Deactivate parent for child ===
  const deactivateParentForChild = useCallback((ancestorCode, childCode) => {
    setTree((prev) => {
      if (undoBatchRef.current && !undoBatchRef.current.snapshot) {
        undoBatchRef.current.snapshot = { ...prev };
      }

      const updated = { ...prev };
      const ancestor = updated[ancestorCode];
      if (!ancestor) return prev;

      // Clear ancestor
      updated[ancestorCode] = {
        ...ancestor,
        active: false,
        quantity: 0, length: 0, width: 0, height: 0,
        unitPrice: 0, unit: "", factor: 1,
        totalComputed: 0,
      };

      // Activate clicked child
      if (updated[childCode]) {
        updated[childCode] = { ...updated[childCode], active: true };
        updated[childCode].totalComputed = calculateTotal(updated[childCode]);
      }

      return aggregateTree(updated);
    });

    setPendingOps((prev) => {
      const codes = [ancestorCode, childCode];
      const next = [...prev];
      for (const c of codes) {
        if (!next.find((op) => op.code === c)) {
          next.push({ code: c, kind: "upsert" });
        }
      }
      return next;
    });
  }, []);

  // === Add Child ===
  const addChild4Digit = useCallback((parentCode) => {
    setTree((prev) => {
      if (undoBatchRef.current && !undoBatchRef.current.snapshot) {
        undoBatchRef.current.snapshot = { ...prev };
      }

      const newCode = getNextChildCode(parentCode, prev);
      if (!newCode) return prev;

      const parent = prev[parentCode];
      if (!parent) return prev;

      // Child level is always parent level + 1. Deriving from code format
      // breaks for overflow codes like "2010" (4 digits, no dot) which would
      // otherwise be mis-detected as level 4 even though they are level 3.
      const level = (parent.level || 0) + 1;

      const newNode = {
        code: newCode,
        name: "",
        source: "",
        length: 0, width: 0, height: 0,
        quantity: 1,
        unit: "pl",
        unitPrice: 0,
        factor: 1,
        totalComputed: 0,
        active: true,
        isUserAdded: true,
        level,
        parentCode,
        children: [],
        phase: parent.phase || "default",
        note: "",
        comment: "",
        isRisk: false,
        isNuessli: false,
        tags: [],
      };

      const updated = { ...prev };
      updated[newCode] = newNode;

      // Deactivate parent
      updated[parentCode] = {
        ...parent,
        active: false,
        quantity: 0, length: 0, width: 0, height: 0,
        unitPrice: 0, unit: "", factor: 1,
        totalComputed: 0,
        children: [...(parent.children || []), newCode],
      };

      // setState-in-setState: vermeidet fragile äussere `let`-Variable und
      // deferred-updater-Timing-Probleme (React 18 concurrent rendering).
      // newCode kann der Code einer zuvor gelöschten Position sein (getNextChildCode
      // vergibt Lücken neu) — eine pendente delete-Op muss dann zur upsert-Op werden.
      setPendingOps((prevOps) => {
        const next = [...prevOps];
        const existingNew = next.findIndex((op) => op.code === newCode);
        if (existingNew >= 0) next[existingNew] = { code: newCode, kind: "upsert" };
        else next.push({ code: newCode, kind: "upsert" });
        if (!next.find((op) => op.code === parentCode)) next.push({ code: parentCode, kind: "upsert" });
        return next;
      });
      setExpanded((prevExp) => ({ ...prevExp, [parentCode]: true }));

      return aggregateTree(updated);
    });
  }, []);

  // === Delete Node ===
  const deleteNode = useCallback((code) => {
    setTree((prev) => {
      const node = prev[code];
      if (!node) return prev;

      if (undoBatchRef.current && !undoBatchRef.current.snapshot) {
        undoBatchRef.current.snapshot = { ...prev };
      }

      const updated = { ...prev };
      const deletedCodes = [];
      // Snapshot der zu löschenden Nodes, damit saveBatch phase/code hat.
      // (Vorher: node: prev[dc] - falsch, weil "prev" im setPendingOps-Closure
      // das pendingOps-Array ist, nicht der Tree.)
      const deletedNodeSnapshots = {};

      const deleteRecursive = (c) => {
        const n = updated[c];
        if (!n) return;
        if (n.children) {
          for (const childCode of n.children) {
            deleteRecursive(childCode);
          }
        }
        deletedCodes.push(c);
        deletedNodeSnapshots[c] = n;
        delete updated[c];
      };

      deleteRecursive(code);

      // Remove from parent
      if (node.parentCode && updated[node.parentCode]) {
        const remainingChildren = (updated[node.parentCode].children || []).filter((c) => c !== code);
        updated[node.parentCode] = {
          ...updated[node.parentCode],
          children: remainingChildren,
          // Re-activate parent if it has no remaining children
          ...(remainingChildren.length === 0 ? { active: true } : {}),
        };
      }

      // Track delete ops
      setPendingOps((prevOps) => {
        const next = [...prevOps];
        for (const dc of deletedCodes) {
          const existing = next.findIndex((op) => op.code === dc);
          if (existing >= 0) {
            next[existing] = { code: dc, kind: "delete", node: deletedNodeSnapshots[dc] };
          } else {
            next.push({ code: dc, kind: "delete", node: deletedNodeSnapshots[dc] });
          }
        }
        if (node.parentCode && !next.find((op) => op.code === node.parentCode)) {
          next.push({ code: node.parentCode, kind: "upsert" });
        }
        return next;
      });

      return aggregateTree(updated);
    });
  }, []);

  // === Toggle Expand ===
  const toggleExpand = useCallback((code) => {
    setExpanded((prev) => ({ ...prev, [code]: !prev[code] }));
  }, []);

  // === Change Level Filter (rebuilds expanded state) ===
  const changeLevelFilter = useCallback((level) => {
    setLevelFilter(level);
    setTree((currentTree) => {
      const newExpanded = {};
      Object.values(currentTree).forEach((node) => {
        // level >= 5: alles einblenden (inkl. potenzieller Level 6+).
        if (level >= 5 || node.level < level) {
          newExpanded[node.code] = true;
        }
      });
      setExpanded(newExpanded);
      return currentTree;
    });
  }, []);

  // === Search/Filter ===
  const addFilter = useCallback((column, value) => {
    setActiveFilters((prev) => [...prev, { column, value }]);
    setSearchTerm("");
  }, []);

  const removeFilter = useCallback((index) => {
    setActiveFilters((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const getFilteredCodes = useCallback(() => {
    if (!searchTerm.trim() && activeFilters.length === 0) return null;

    const matchingCodes = new Set();

    const getParentCodes = (node) => {
      const parents = [];
      let current = node;
      while (current && current.parentCode) {
        parents.push(current.parentCode);
        current = tree[current.parentCode];
        if (!current) break;
      }
      return parents;
    };

    const fieldPrefixes = {
      "source:": "source",
      "tag:": "tags",
      "ep:": "unitPrice",
      "total:": "totalComputed",
    };

    Object.values(tree).forEach((node) => {
      let matchesSearch = true;
      let matchesFilters = true;

      if (searchTerm.trim()) {
        const queryLower = searchTerm.toLowerCase().trim();
        let query = searchTerm.trim();
        let specificField = null;

        for (const [prefix, field] of Object.entries(fieldPrefixes)) {
          if (query.toLowerCase().startsWith(prefix)) {
            specificField = field;
            query = query.substring(prefix.length).trim();
            break;
          }
        }

        if (specificField) {
          if (specificField === "tags") {
            const tags = node.tags || [];
            matchesSearch = tags.some((t) => t.toLowerCase().includes(query.toLowerCase()));
          } else if (specificField === "unitPrice" || specificField === "totalComputed") {
            matchesSearch = node[specificField] != null && node[specificField].toString().includes(query);
          } else {
            matchesSearch = node[specificField] && node[specificField].toLowerCase().includes(query.toLowerCase());
          }
        } else {
          const tags = (node.tags || []).map((t) => t.toLowerCase());
          matchesSearch =
            node.code.toLowerCase().includes(queryLower) ||
            (node.name && node.name.toLowerCase().includes(queryLower)) ||
            (node.source && node.source.toLowerCase().includes(queryLower)) ||
            tags.some((t) => t.includes(queryLower));
        }
      }

      if (activeFilters.length > 0) {
        matchesFilters = activeFilters.every((filter) => {
          const col = filter.column.toLowerCase();
          const val = filter.value.toLowerCase();
          if (col === "source") return node.source && node.source.toLowerCase().includes(val);
          if (col === "tag") return (node.tags || []).some((t) => t.toLowerCase().includes(val));
          if (col === "ep") return node.unitPrice != null && node.unitPrice.toString().includes(filter.value);
          if (col === "total") return node.totalComputed != null && node.totalComputed.toString().includes(filter.value);
          // Default: search name
          return node.name && node.name.toLowerCase().includes(val);
        });
      }

      if (matchesSearch && matchesFilters) {
        matchingCodes.add(node.code);
        getParentCodes(node).forEach((pc) => matchingCodes.add(pc));
      }
    });

    return matchingCodes;
  }, [tree, searchTerm, activeFilters]);

  // === Save Batch ===
  const saveBatchInternal = useCallback(async (kind = "manual") => {
    if (!provider || !estimateIdRef.current) return;
    if (pendingOps.length === 0 && pendingColumnOps.length === 0) return;

    const opsToSave = [...pendingOps];
    const colOpsToSave = [...pendingColumnOps];
    setIsSaving(true);

    try {
      // Save custom column definitions
      if (colOpsToSave.length > 0 && provider.saveCustomColumnsBatch) {
        await provider.saveCustomColumnsBatch(estimateIdRef.current, colOpsToSave);
        setPendingColumnOps((prev) => {
          const savedIds = new Set(colOpsToSave.map((op) => op.column.id));
          return prev.filter((op) => !savedIds.has(op.column.id));
        });
      }

      // Save node ops
      if (opsToSave.length > 0) {
        // Read latest tree state via setTree updater, then await the save
        const fullOps = await new Promise((resolve) => {
          setTree((currentTree) => {
            const ops = opsToSave.map((op) => {
              if (op.kind === "delete") {
                return { kind: "delete", node: op.node || { code: op.code, phase: currentPhase } };
              }
              return { kind: "upsert", node: currentTree[op.code] };
            }).filter((op) => op.node);
            resolve(ops);
            return currentTree;
          });
        });

        await provider.saveBatch(estimateIdRef.current, fullOps);
        setPendingOps((prev) => {
          const savedCodes = new Set(opsToSave.map((op) => op.code));
          return prev.filter((op) => !savedCodes.has(op.code));
        });
      }
      setLastSavedAt(new Date());
      setLastSaveKind(kind);
    } catch (err) {
      console.error("Save error:", err);
      setError(err.message);
      // Weiterreichen, damit Aufrufer (z.B. switchPhase) den Fehler behandeln koennen.
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [provider, pendingOps, pendingColumnOps, currentPhase]);

  // Exportierte saveBatch: schluckt Fehler (UI-State wird via setError angezeigt),
  // damit externe Aufrufer (Auto-save, Ctrl+S, Save-Button) keine unhandled rejections werfen.
  // saveBatchInternal wirft weiterhin und wird von switchPhase für Abbruchsbestätigung genutzt.
  // kind ist optional; da saveBatch teils direkt als onClick-Handler haengt, wird
  // alles ausser dem expliziten "auto" als manuelles Speichern gewertet.
  // Gibt true zurueck, wenn gespeichert werden konnte — der Verlassen-Dialog
  // entscheidet daran, ob er weiternavigieren darf.
  const saveBatch = useCallback(async (kind) => {
    try {
      await saveBatchInternal(kind === "auto" ? "auto" : "manual");
      return true;
    } catch {
      // Fehler wurde bereits geloggt und via setError gemeldet.
      return false;
    }
  }, [saveBatchInternal]);

  // === Auto-Save ===
  // saveBatch aendert bei jeder Aenderung die Identitaet; ueber die Ref laeuft der
  // Timer trotzdem immer gegen die aktuelle Funktion.
  const saveBatchRef = useRef(saveBatch);
  useEffect(() => {
    saveBatchRef.current = saveBatch;
  }, [saveBatch]);

  // 1. Bearbeitungspause: 30 Sekunden nach der letzten Aenderung speichern.
  //    pendingOps/pendingColumnOps sind bei jeder Aenderung neue Arrays, der
  //    Timer wird also mit jeder Eingabe neu gestartet.
  useEffect(() => {
    if (!isDirty || isSaving || !estimateIdRef.current) return;

    const timer = setTimeout(() => {
      saveBatchRef.current("auto");
    }, AUTO_SAVE_IDLE_MS);

    return () => clearTimeout(timer);
  }, [pendingOps, pendingColumnOps, isDirty, isSaving]);

  // 2. Notbremse: spaetestens 10 Minuten nach Beginn der Bearbeitung speichern,
  //    auch wenn ohne Pause weitergearbeitet wird. dirtySinceRef haelt den Start
  //    fest, damit die Frist von Eingaben nicht verlaengert wird.
  const dirtySinceRef = useRef(null);
  const maxAgeSaveDoneRef = useRef(false);
  useEffect(() => {
    if (!isDirty) {
      dirtySinceRef.current = null;
      maxAgeSaveDoneRef.current = false;
      return;
    }
    if (!estimateIdRef.current) return;
    if (dirtySinceRef.current === null) dirtySinceRef.current = Date.now();
    // Nach einem fehlgeschlagenen Versuch nicht sofort erneut ausloesen (die
    // Frist ist dann abgelaufen) — der 30-Sekunden-Timer versucht es weiter.
    if (isSaving || maxAgeSaveDoneRef.current) return;

    const due = dirtySinceRef.current + AUTO_SAVE_MAX_AGE_MS;
    const timer = setTimeout(() => {
      maxAgeSaveDoneRef.current = true;
      saveBatchRef.current("auto");
    }, Math.max(0, due - Date.now()));

    return () => clearTimeout(timer);
  }, [isDirty, isSaving]);

  // Fenster schliessen/neu laden mit ungespeicherten Aenderungen: der Browser
  // zeigt seinen eigenen Bestaetigungsdialog (Text ist nicht beeinflussbar).
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Baum/Varianten/Kontroll-Meta einer Phase in den State laden — gemeinsamer
  // Kern von switchPhase und renamePhase (init lädt zusätzlich Custom Columns).
  const loadPhaseState = useCallback(async (estimateId, phaseName) => {
    const isControlPhase = phaseName.trim().startsWith("41");
    const [loaded, , controlMeta] = await Promise.all([
      provider.load(estimateId, phaseName),
      loadVariants(estimateId, phaseName),
      isControlPhase && provider.loadControlMeta
        ? provider.loadControlMeta(estimateId, phaseName)
        : { kvBaseImported: false, objektgliederung: null },
    ]);
    const aggregated = aggregateTree(loaded);
    setTree(aggregated);
    setPendingOps([]);
    setUndoStack([]);
    setKvBaseImported(!!controlMeta?.kvBaseImported);
    setObjektgliederung(controlMeta?.objektgliederung ?? null);
    const initExpanded = {};
    Object.values(aggregated).forEach((node) => {
      // levelFilter >= 5: alles einblenden (inkl. potenzieller Level 6+).
      if (levelFilter >= 5 || node.level < levelFilter) {
        initExpanded[node.code] = true;
      }
    });
    setExpanded(initExpanded);
  }, [provider, loadVariants, levelFilter]);

  // Objektgliederung der aktuellen Kostenkontroll-Phase speichern. Direkt
  // persistiert (nicht über pendingOps/Speichern-Button), wie bisher am Dossier.
  const saveObjektgliederung = useCallback(async (value) => {
    if (!provider?.saveControlObjektgliederung || !estimateIdRef.current) return;
    const phase = currentPhase;
    if (!(phase || "").trim().startsWith("41")) return;
    const saved = await provider.saveControlObjektgliederung(estimateIdRef.current, phase, value);
    // Während des Speicherns auf eine andere Phase gewechselt: deren Wert nicht überschreiben.
    if (currentPhaseRef.current === phase) setObjektgliederung(saved);
    return saved;
  }, [provider, currentPhase]);

  // === Switch Phase ===
  const switchPhase = useCallback(async (phaseName) => {
    if (!provider || !estimateIdRef.current || phaseName === currentPhase) return;

    // Save pending ops first — bei Fehler den Nutzer fragen, damit nicht silent verloren geht.
    if (pendingOps.length > 0) {
      try {
        await saveBatchInternal();
      } catch (err) {
        const msg = err?.message || "Unbekannter Fehler";
        const proceed = window.confirm(
          `Speichern fehlgeschlagen (${msg}).\n` +
          `Beim Phasenwechsel gehen ungespeicherte Aenderungen verloren. Trotzdem fortfahren?`
        );
        if (!proceed) return;
      }
    }

    setCurrentPhase(phaseName);
    setIsLoading(true);
    setError(null);

    try {
      // Update dossier record with new currentPhase
      if (estimateRef.current) {
        await updateEstimate(estimateIdRef.current, {
          ...estimateRef.current,
          currentPhase: phaseName,
        });
        estimateRef.current = { ...estimateRef.current, currentPhase: phaseName };
      }

      await loadPhaseState(estimateIdRef.current, phaseName);
    } catch (err) {
      console.error("Switch phase error:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [provider, currentPhase, pendingOps, saveBatchInternal, loadPhaseState]);

  // === Add Phase ===
  // US-KOST-019: optional mit Datenübernahme aus einer bestehenden Phase.
  // Übernahme-Wege, abhängig von Quelle und Ziel:
  //   - Ziel ist eine Kostenkontrolle ("41 ..."), Quelle nicht: Die Quelle wird
  //     als KV-BASIS importiert (KOST-006-Semantik: aggregierter Total x 1.081
  //     als fixer KV-Wert, kvBaseImported-Flag gesetzt) — eine Roh-Kopie der
  //     Ausmass-Items wäre in der Kostenkontrolle wertlos (KV überall 0).
  //   - Kontrolle→Kontrolle: VOLLSTÄNDIGE Arbeitskopie — Items inkl. Meta-Zeile
  //     PLUS Verträge/Nachträge und Zahlungen (neue RowKey-UUIDs, unabhängig
  //     editierbar). Die Ziel-Phase wird vorher geleert, damit ein wiederholter
  //     Versuch nach einem Teilfehler keine Duplikate erzeugt.
  //   - Schätzung→Schätzung: Roh-Klon der Items via copyPhaseItems.
  // Wirft bei Validierungs-/Speicherfehlern, damit die UI sie anzeigen kann.
  const addPhase = useCallback(async (phaseName, { copyFromPhase } = {}) => {
    const trimmed = (phaseName || "").trim();
    if (!trimmed || !estimateIdRef.current) return false;
    if (phases.includes(trimmed)) {
      throw new Error(`Phase "${trimmed}" existiert bereits.`);
    }
    if (copyFromPhase && !phases.includes(copyFromPhase)) {
      throw new Error(`Quell-Phase "${copyFromPhase}" existiert nicht.`);
    }

    // Ungespeicherte Änderungen zuerst sichern — die Quelle der Datenübernahme
    // kann die aktuell bearbeitete Phase sein (Import/Klon lesen aus Azure).
    if (pendingOps.length > 0 || pendingColumnOps.length > 0) {
      await saveBatchInternal();
    }

    if (copyFromPhase) {
      const targetIs41 = trimmed.startsWith("41");
      const sourceIs41 = copyFromPhase.trim().startsWith("41");
      if (targetIs41 && !sourceIs41) {
        // KV-Basis-Import in die neue Phase (analog importKvBase, aber ohne
        // Bezug auf den aktuell geladenen Baum — die neue Phase ist leer).
        const source = aggregateTree(await provider.load(estimateIdRef.current, copyFromPhase));
        const ops = Object.values(source)
          .filter((src) => src.active || src.isUserAdded)
          .map((src) => ({
            kind: "upsert",
            node: {
              ...src,
              kvFixed: src.active
                ? Math.round((src.totalComputed || 0) * MWST_FACTOR * 100) / 100
                : 0,
              erwartung: 0,
              active: !!src.active,
              totalComputed: 0,
              phase: trimmed,
            },
          }));
        await provider.saveBatch(estimateIdRef.current, ops);
        if (provider.saveControlMeta) {
          await provider.saveControlMeta(estimateIdRef.current, trimmed, { kvBaseImported: true });
        }
      } else {
        if (provider?.copyPhaseItems) {
          await provider.copyPhaseItems(estimateIdRef.current, copyFromPhase, trimmed);
        }
        if (targetIs41 && sourceIs41) {
          // Vollständige Arbeitskopie: Verträge/Zahlungen mitkopieren. Vorher
          // Ziel leeren — Reste könnten nur von einem abgebrochenen früheren
          // Versuch stammen (die Phase ist neu), würden aber sonst doppeln.
          await deleteContractsPhase(estimateIdRef.current, trimmed);
          await deletePaymentsPhase(estimateIdRef.current, trimmed);
          await copyContractsPhase(estimateIdRef.current, copyFromPhase, trimmed);
          await copyPaymentsPhase(estimateIdRef.current, copyFromPhase, trimmed);
        }
      }
    }

    const next = [...phases, trimmed];
    if (estimateRef.current) {
      const updatedEstimate = { ...estimateRef.current, phases: next };
      await updateEstimate(estimateIdRef.current, updatedEstimate);
      estimateRef.current = updatedEstimate;
    }
    setPhases(next);
    return true;
  }, [phases, pendingOps, pendingColumnOps, saveBatchInternal, provider]);

  // === Rename Phase (US-KOST-019) ===
  // Der Phasenname steckt in RowKeys (costEstimateItems: `${phase}__${code}` und
  // `__ctrlmeta__${phase}`) sowie in Phase-Properties (Verträge, Zahlungen,
  // Varianten). Umbenennen ist deshalb eine Migration: Items copy-then-delete im
  // Provider, der Rest per Property-Update. Bricht der Vorgang ab, geht nichts
  // verloren; ein erneuter Versuch mit denselben Namen ist idempotent.
  const renamePhase = useCallback(async (oldName, newName) => {
    const trimmed = (newName || "").trim();
    if (!oldName || !trimmed || trimmed === oldName || !estimateIdRef.current) return false;
    if (!phases.includes(oldName)) {
      throw new Error(`Phase "${oldName}" existiert nicht.`);
    }
    if (phases.includes(trimmed)) {
      throw new Error(`Phase "${trimmed}" existiert bereits.`);
    }

    // Ungespeicherte Änderungen sichern, sonst würden sie nach der Migration
    // unter dem alten Phasennamen (alte RowKeys) gespeichert.
    if (pendingOps.length > 0 || pendingColumnOps.length > 0) {
      await saveBatchInternal();
    }

    setIsLoading(true);
    setError(null);
    try {
      await provider.renamePhaseItems(estimateIdRef.current, oldName, trimmed);
      await renameContractsPhase(estimateIdRef.current, oldName, trimmed);
      await renamePaymentsPhase(estimateIdRef.current, oldName, trimmed);
      await renameVariantsPhase(estimateIdRef.current, oldName, trimmed);

      const nextPhases = phases.map((p) => (p === oldName ? trimmed : p));
      if (estimateRef.current) {
        const updatedEstimate = {
          ...estimateRef.current,
          phases: nextPhases,
          currentPhase:
            estimateRef.current.currentPhase === oldName ? trimmed : estimateRef.current.currentPhase,
        };
        await updateEstimate(estimateIdRef.current, updatedEstimate);
        estimateRef.current = updatedEstimate;
      }
      setPhases(nextPhases);

      if (currentPhase === oldName) {
        setCurrentPhase(trimmed);
        await loadPhaseState(estimateIdRef.current, trimmed);
      }
      return true;
    } catch (err) {
      console.error("Rename phase error:", err);
      throw new Error(
        `Phase konnte nicht umbenannt werden: ${err?.message || "Unbekannter Fehler"}. ` +
          "Es sind keine Daten verloren gegangen — der Vorgang kann gefahrlos wiederholt werden."
      );
    } finally {
      setIsLoading(false);
    }
  }, [phases, pendingOps, pendingColumnOps, saveBatchInternal, provider, currentPhase, loadPhaseState]);

  // === Delete Phase (US-KOST-019) ===
  // Löscht eine Phase endgültig: alle BKP-Items inkl. Kostenkontroll-Meta,
  // Verträge, Zahlungen und Varianten der Phase sowie den Eintrag in der
  // Phasen-Liste des Dossiers. Die letzte Phase ist geschützt. Jeder Schritt ist
  // idempotent — schlägt einer fehl, bleibt die Phase in der Liste und der
  // Vorgang kann gefahrlos wiederholt werden. Wird die aktuell geöffnete Phase
  // gelöscht, wechselt der Editor auf die davorliegende Phase der Liste;
  // pendente Ops der gelöschten Phase werden verworfen (loadPhaseState).
  // Rückgabe: Name der neuen aktuellen Phase, wenn gewechselt wurde, sonst null.
  const deletePhase = useCallback(async (phaseName) => {
    if (!phaseName || !estimateIdRef.current) return null;
    if (!phases.includes(phaseName)) {
      throw new Error(`Phase "${phaseName}" existiert nicht.`);
    }
    if (phases.length <= 1) {
      throw new Error("Die letzte Phase eines Dossiers kann nicht gelöscht werden.");
    }

    const isCurrent = currentPhase === phaseName;
    const nextPhases = phases.filter((p) => p !== phaseName);
    const fallback = isCurrent
      ? nextPhases[Math.max(0, phases.indexOf(phaseName) - 1)]
      : null;

    setIsLoading(true);
    setError(null);
    try {
      await provider.deletePhaseItems(estimateIdRef.current, phaseName);
      await deleteContractsPhase(estimateIdRef.current, phaseName);
      await deletePaymentsPhase(estimateIdRef.current, phaseName);
      await deleteVariantsPhase(estimateIdRef.current, phaseName);

      if (estimateRef.current) {
        const updatedEstimate = {
          ...estimateRef.current,
          phases: nextPhases,
          currentPhase:
            estimateRef.current.currentPhase === phaseName
              ? fallback || nextPhases[0]
              : estimateRef.current.currentPhase,
        };
        await updateEstimate(estimateIdRef.current, updatedEstimate);
        estimateRef.current = updatedEstimate;
      }
      setPhases(nextPhases);

      if (isCurrent) {
        setCurrentPhase(fallback);
        await loadPhaseState(estimateIdRef.current, fallback);
      }
      return fallback;
    } catch (err) {
      console.error("Delete phase error:", err);
      throw new Error(
        `Phase konnte nicht gelöscht werden: ${err?.message || "Unbekannter Fehler"}. ` +
          "Der Vorgang kann gefahrlos wiederholt werden."
      );
    } finally {
      setIsLoading(false);
    }
  }, [phases, currentPhase, provider, loadPhaseState]);

  // === Variant CRUD ===

  const createVariant = useCallback(async (name) => {
    if (!estimateIdRef.current || !name?.trim()) return null;
    try {
      const saved = await saveVariantApi(estimateIdRef.current, {
        name: name.trim(),
        phase: currentPhase,
        isActive: false,
        positions: [],
      });
      setVariants((prev) => [...prev, saved]);
      return saved;
    } catch (err) {
      console.error("Create variant error:", err);
      setError(err.message);
      return null;
    }
  }, [currentPhase]);

  const toggleVariant = useCallback(async (variantId) => {
    setVariants((prev) => {
      const updated = prev.map((v) =>
        v.id === variantId ? { ...v, isActive: !v.isActive } : v
      );

      // Persist toggle
      const variant = updated.find((v) => v.id === variantId);
      if (variant && estimateIdRef.current) {
        updateVariantApi(estimateIdRef.current, variantId, variant).catch((err) =>
          console.error("Toggle variant error:", err)
        );
      }

      return updated;
    });
  }, []);

  const removeVariant = useCallback(async (variantId) => {
    if (!estimateIdRef.current) return;
    try {
      await deleteVariantApi(estimateIdRef.current, variantId);
      setVariants((prev) => prev.filter((v) => v.id !== variantId));
    } catch (err) {
      console.error("Delete variant error:", err);
      setError(err.message);
    }
  }, []);

  const addPositionToVariant = useCallback(async (variantId, bkpCode, overrides) => {
    setVariants((prev) => {
      const updated = prev.map((v) => {
        if (v.id !== variantId) return v;

        const existingIdx = v.positions.findIndex((p) => p.bkpCode === bkpCode);
        let newPositions;
        if (existingIdx >= 0) {
          newPositions = [...v.positions];
          newPositions[existingIdx] = { bkpCode, overrides };
        } else {
          newPositions = [...v.positions, { bkpCode, overrides }];
        }

        const updatedVariant = { ...v, positions: newPositions };

        // Persist
        if (estimateIdRef.current) {
          updateVariantApi(estimateIdRef.current, variantId, updatedVariant).catch((err) =>
            console.error("Add position to variant error:", err)
          );
        }

        return updatedVariant;
      });

      return updated;
    });
  }, []);

  const removePositionFromVariant = useCallback(async (variantId, bkpCode) => {
    setVariants((prev) => {
      const updated = prev.map((v) => {
        if (v.id !== variantId) return v;

        const newPositions = v.positions.filter((p) => p.bkpCode !== bkpCode);
        const updatedVariant = { ...v, positions: newPositions };

        if (estimateIdRef.current) {
          updateVariantApi(estimateIdRef.current, variantId, updatedVariant).catch((err) =>
            console.error("Remove position from variant error:", err)
          );
        }

        return updatedVariant;
      });

      return updated;
    });
  }, []);

  // === Apply Variant (Übernehmen) ===
  // Writes variant overrides into the base tree, marks affected nodes dirty, then deletes the variant
  const applyVariant = useCallback(async (variantId) => {
    const variant = variants.find((v) => v.id === variantId);
    if (!variant || variant.positions.length === 0) return;

    // Apply overrides to tree
    setTree((prev) => {
      const updated = { ...prev };
      const ops = [];

      for (const pos of variant.positions) {
        const node = updated[pos.bkpCode];
        if (!node) continue;

        const overrides = pos.overrides || {};
        const mergedNode = { ...node };

        for (const [field, value] of Object.entries(overrides)) {
          const parsedValue = ["quantity", "length", "width", "height", "unitPrice", "factor"].includes(field)
            ? (parseFloat(value) || 0)
            : value;
          mergedNode[field] = parsedValue;
        }

        mergedNode.totalComputed = calculateTotal(mergedNode);
        updated[pos.bkpCode] = mergedNode;
        ops.push({ code: pos.bkpCode, kind: "upsert" });
      }

      // Mark affected nodes dirty
      setPendingOps((prev) => {
        const next = [...prev];
        for (const op of ops) {
          if (!next.find((o) => o.code === op.code)) next.push(op);
        }
        return next;
      });

      return aggregateTree(updated);
    });

    // Delete the variant
    if (estimateIdRef.current) {
      try {
        await deleteVariantApi(estimateIdRef.current, variantId);
      } catch (err) {
        console.error("Delete variant after apply error:", err);
      }
    }
    setVariants((prev) => prev.filter((v) => v.id !== variantId));
  }, [variants]);

  // === Undo ===
  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.type === "batch" && last.snapshot) {
        setTree(aggregateTree(last.snapshot));
        // Restore pendingOps to the state before the batch started
        if (last.pendingOps) {
          setPendingOps(last.pendingOps);
        }
      }
      return prev.slice(0, -1);
    });
  }, []);

  // === Custom Columns ===
  const addCustomColumn = useCallback((name) => {
    const id = `col_${crypto.randomUUID()}`;
    const maxOrder = customColumns.length > 0
      ? Math.max(...customColumns.map((c) => c.order))
      : -1;
    const newCol = {
      id,
      name,
      type: "tags",
      order: maxOrder + 1,
      hidden: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setCustomColumns((prev) => [...prev, newCol]);
    setPendingColumnOps((prev) => [...prev, { kind: "upsert", column: newCol }]);
  }, [customColumns]);

  const deleteCustomColumn = useCallback((colId) => {
    setCustomColumns((prev) => prev.filter((c) => c.id !== colId));
    setPendingColumnOps((prev) => {
      const col = customColumns.find((c) => c.id === colId);
      if (!col) return prev;
      return [...prev.filter((op) => op.column.id !== colId), { kind: "delete", column: col }];
    });
    // Remove custom field values from all nodes
    setTree((prev) => {
      const updated = { ...prev };
      for (const code of Object.keys(updated)) {
        const node = updated[code];
        if (node.customFields && node.customFields[colId] !== undefined) {
          const newFields = { ...node.customFields };
          delete newFields[colId];
          updated[code] = { ...node, customFields: newFields };
          setPendingOps((ops) => {
            const exists = ops.find((op) => op.code === code);
            if (exists) return ops;
            return [...ops, { kind: "upsert", code }];
          });
        }
      }
      return updated;
    });
  }, [customColumns]);

  const renameCustomColumn = useCallback((colId, newName) => {
    setCustomColumns((prev) => prev.map((c) =>
      c.id === colId ? { ...c, name: newName, updatedAt: new Date().toISOString() } : c
    ));
    setPendingColumnOps((prev) => {
      const col = customColumns.find((c) => c.id === colId);
      if (!col) return prev;
      const updated = { ...col, name: newName, updatedAt: new Date().toISOString() };
      return [...prev.filter((op) => op.column.id !== colId), { kind: "upsert", column: updated }];
    });
  }, [customColumns]);

  const editCustomField = useCallback((code, colId, value) => {
    setTree((prev) => {
      const node = prev[code];
      if (!node) return prev;
      const customFields = { ...(node.customFields || {}), [colId]: value };
      return { ...prev, [code]: { ...node, customFields } };
    });
    setPendingOps((prev) => {
      const exists = prev.find((op) => op.code === code);
      if (exists) return prev;
      return [...prev, { kind: "upsert", code }];
    });
  }, []);

  // === Kostenkontrolle (Phase 41) ===

  // KOST-006: KV-Basis aus einer früheren Kostenschätzung übernehmen.
  // Quelle wählbar (sourceEstimateId + sourcePhase) aus allen Dossiers desselben
  // Projekts; Default: Phase 32 des aktuellen Dossiers. Pro in der Quelle direkt
  // bewerteter (aktiver) BKP-Position wird deren aggregierter Total x 1.081 (MwSt)
  // als fixer KV-Wert in die aktuelle Phase-41-Position geschrieben. Aggregat-/
  // ungenutzte Positionen erhalten KvFixed 0 (ihr KV rollt im Editor aus den Kindern
  // auf). Nur veränderte Positionen werden persistiert; ein Meta-Flag verhindert
  // erneute Anzeige der Primäraktion.
  //
  // US-KOST-019-Ergänzung: Auch eine KOSTENKONTROLLE ("41 ...") ist als Quelle
  // zulässig ("neue Kostenkontrolle auf Basis einer alten"). Deren Werte sind
  // bereits inkl. MwSt und werden 1:1 übernommen — je nach `mode` der KV der
  // Quelle (Default) oder deren aktuelle Prognose als neuer KV (Re-Baseline;
  // braucht Verträge/Zahlungen der Quelle für computeControlTotals).
  const importKvBase = useCallback(async ({ sourceEstimateId, sourcePhase, mode } = {}) => {
    if (!provider || !estimateIdRef.current) return;
    const srcEstimateId = sourceEstimateId || estimateIdRef.current;
    const srcPhase = sourcePhase || phases.find((p) => p.trim().startsWith("32"));
    if (!srcPhase) {
      throw new Error("Keine Quell-Phase gewählt. KV-Basis kann nicht übernommen werden.");
    }
    const targetPhase = currentPhase;
    setIsSaving(true);
    setError(null);
    try {
      const p32 = aggregateTree(await provider.load(srcEstimateId, srcPhase));

      const srcIs41 = srcPhase.trim().startsWith("41");
      let sourceTotals = null;
      if (srcIs41 && mode === "prognose") {
        const [contracts, payments] = await Promise.all([
          getContractsByEstimate(srcEstimateId, srcPhase),
          getPaymentsByEstimate(srcEstimateId, srcPhase),
        ]);
        const groupByCode = (list) => {
          const map = {};
          for (const item of list) {
            if (!map[item.code]) map[item.code] = [];
            map[item.code].push(item);
          }
          return map;
        };
        sourceTotals = computeControlTotals(p32, {
          contractsByCode: groupByCode(contracts),
          paymentsByCode: groupByCode(payments),
        }).totals;
      }

      const kvFromSource = (src) => {
        if (!src.active) return 0;
        if (srcIs41) {
          const value =
            mode === "prognose"
              ? sourceTotals?.[src.code]?.prognose || 0
              : parseFloat(src.kvFixed) || 0;
          return Math.round(value * 100) / 100;
        }
        return Math.round((src.totalComputed || 0) * MWST_FACTOR * 100) / 100;
      };

      let updatedTree = null;
      const affectedCodes = new Set();
      await new Promise((resolve) => {
        setTree((prev) => {
          // Bereits aktive Phase-41-Positionen mitnehmen, damit ein erneutes
          // Übernehmen (Overwrite) alte Werte sauber überschreibt.
          Object.values(prev).forEach((n) => {
            if (n.active) affectedCodes.add(n.code);
          });

          // Phase-41-Baum als Struktur-Snapshot von Phase 32 aufbauen. So kommen
          // auch in Phase 32 ergänzte (user-added) Unterpositionen mit, die nicht
          // in bkp.csv stehen. Bestehende Erwartung wird beibehalten.
          const next = {};
          Object.values(p32).forEach((src) => {
            const prevNode = prev[src.code];
            const isDirect = !!src.active;
            const kv = kvFromSource(src);
            next[src.code] = {
              ...src,
              kvFixed: kv,
              erwartung: prevNode?.erwartung || 0,
              active: isDirect,
              totalComputed: 0,
              phase: targetPhase,
            };
            // Aktive Positionen sowie user-added Nodes (inkl. ihrer Ahnenkette)
            // müssen persistiert werden, damit der Baum beim Reload vollständig ist
            // (Standard-BKP-Aggregate kommen ohnehin aus bkp.csv).
            if (isDirect || src.isUserAdded) affectedCodes.add(src.code);
          });
          updatedTree = next;
          resolve();
          return next;
        });
      });

      const ops = [...affectedCodes]
        .map((code) => updatedTree[code])
        .filter(Boolean)
        .map((node) => ({ kind: "upsert", node: { ...node, phase: targetPhase } }));

      await provider.saveBatch(estimateIdRef.current, ops);
      if (provider.saveControlMeta) {
        await provider.saveControlMeta(estimateIdRef.current, targetPhase, { kvBaseImported: true });
      }
      setKvBaseImported(true);
      setLastSavedAt(new Date());
      setLastSaveKind("manual");
    } catch (err) {
      console.error("KV-Basis import error:", err);
      setError(err.message);
      throw new Error(`KV-Basis konnte nicht übernommen werden: ${err?.message || "Unbekannter Fehler"}`);
    } finally {
      setIsSaving(false);
    }
  }, [provider, phases, currentPhase]);

  // KOST-007/011/018: fixen KV-Wert, Erwartung oder Bezeichnung einer Position setzen.
  // Numerische Edits aktivieren die Position (sie wird zur genutzten Blattposition);
  // Name und Kommentar sind reine Textfelder und dürfen den Aktiv-Zustand nicht
  // verändern — sonst würde das Umbenennen/Kommentieren eines Aggregats dessen
  // Rollup zerstören.
  const editControlField = useCallback((code, field, value) => {
    setTree((prev) => {
      const node = prev[code];
      if (!node) return prev;
      if (undoBatchRef.current && !undoBatchRef.current.snapshot) {
        undoBatchRef.current.snapshot = { ...prev };
      }
      if (field === "name" || field === "comment") {
        return { ...prev, [code]: { ...node, [field]: value } };
      }
      const num = parseFloat(value) || 0;
      return { ...prev, [code]: { ...node, [field]: num, active: true } };
    });
    setPendingOps((prev) => {
      if (prev.find((op) => op.code === code)) return prev;
      return [...prev, { code, kind: "upsert" }];
    });
  }, []);

  // KOST-011/012: ungenutzte Position "verwenden" (sichtbar + editierbar machen).
  const activatePosition = useCallback((code) => {
    setTree((prev) => {
      const node = prev[code];
      if (!node || node.active) return prev;
      return { ...prev, [code]: { ...node, active: true } };
    });
    setPendingOps((prev) => {
      if (prev.find((op) => op.code === code)) return prev;
      return [...prev, { code, kind: "upsert" }];
    });
  }, []);

  // KOST-017: Position in der Kostenkontrolle löschen (KV-Anpassung).
  // Standard-BKP-Positionen können nicht wirklich verschwinden — sie kommen beim
  // Laden immer als CSV-Basis zurück. Löschen heisst hier: deaktivieren und
  // KV/Erwartung nullen, womit die Zeile wieder "ungenutzt" ist. User-added
  // Positionen werden echt gelöscht (Entity + Baum). Verliert die Elternposition
  // dadurch ihren letzten aktiven Nachkommen, wird sie selbst aktiviert und ihr
  // KV/Erwartung damit direkt editierbar.
  const deleteControlPosition = useCallback((code) => {
    setTree((prev) => {
      const node = prev[code];
      if (!node) return prev;

      if (undoBatchRef.current && !undoBatchRef.current.snapshot) {
        undoBatchRef.current.snapshot = { ...prev };
      }

      const updated = { ...prev };
      const deleteOps = [];
      const upsertCodes = new Set();

      const removeRecursive = (c) => {
        const n = updated[c];
        if (!n) return;
        for (const childCode of n.children || []) removeRecursive(childCode);
        if (n.isUserAdded) {
          deleteOps.push({ code: c, kind: "delete", node: n });
          delete updated[c];
        } else {
          // Nur Positionen persistieren, die überhaupt Werte trugen — sonst
          // entstünden beim Löschen grosser Aggregate hunderte Leer-Entities.
          const touched =
            n.active || (parseFloat(n.kvFixed) || 0) !== 0 || (parseFloat(n.erwartung) || 0) !== 0;
          updated[c] = {
            ...n,
            active: false,
            kvFixed: 0,
            erwartung: 0,
            children: (n.children || []).filter((cc) => updated[cc]),
          };
          if (touched) upsertCodes.add(c);
        }
      };

      removeRecursive(code);

      if (node.parentCode && updated[node.parentCode]) {
        const parent = updated[node.parentCode];
        const hasActiveDescendant = (pc) =>
          (updated[pc]?.children || []).some((cc) => {
            const child = updated[cc];
            return child && (child.active || hasActiveDescendant(cc));
          });
        const activateParent = !parent.active && !hasActiveDescendant(node.parentCode);
        updated[node.parentCode] = {
          ...parent,
          children: (parent.children || []).filter((cc) => updated[cc]),
          ...(activateParent ? { active: true } : {}),
        };
        if (activateParent) upsertCodes.add(node.parentCode);
      }

      setPendingOps((prevOps) => {
        const next = [...prevOps];
        for (const op of deleteOps) {
          const existing = next.findIndex((o) => o.code === op.code);
          if (existing >= 0) next[existing] = op;
          else next.push(op);
        }
        for (const c of upsertCodes) {
          const existing = next.findIndex((o) => o.code === c);
          if (existing >= 0) next[existing] = { code: c, kind: "upsert" };
          else next.push({ code: c, kind: "upsert" });
        }
        return next;
      });

      return updated;
    });
  }, []);

  return {
    tree,
    pendingOps,
    undoStack,
    expanded,
    setExpanded,
    levelFilter,
    setLevelFilter: changeLevelFilter,
    showInactive,
    setShowInactive,
    searchTerm,
    setSearchTerm,
    activeFilters,
    addFilter,
    removeFilter,
    getFilteredCodes,
    filterSums,
    setFilterSums,
    isSaving,
    lastSavedAt,
    lastSaveKind,
    isDirty,
    isLoading,
    error,
    currentPhase,
    phases,
    // Variant state
    variants,
    getVariantConflicts,
    getEffectiveValue,
    applyVariantOverrides,
    // Methods
    init,
    editCell,
    toggleActive,
    deactivateParentForChild,
    addChild4Digit,
    deleteNode,
    toggleExpand,
    saveBatch,
    switchPhase,
    addPhase,
    renamePhase,
    deletePhase,
    undo,
    beginUndoBatch,
    endUndoBatch,
    // Variant methods
    createVariant,
    toggleVariant,
    removeVariant,
    addPositionToVariant,
    removePositionFromVariant,
    applyVariant,
    // Custom columns
    customColumns,
    addCustomColumn,
    deleteCustomColumn,
    renameCustomColumn,
    editCustomField,
    // Kostenkontrolle (Phase 41)
    kvBaseImported,
    importKvBase,
    objektgliederung,
    saveObjektgliederung,
    editControlField,
    activatePosition,
    deleteControlPosition,
    addChild: addChild4Digit,
  };
}
